import {memo, useCallback, useEffect, useRef, useState} from 'react';
import useTranslation from 'next-translate/useTranslation';

import {useContactUsModal} from '@/components/global/providers/ContactUsModalProvider';
import Modal from '@/components/ui/Modal';
import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useIdentity from '@/hooks/useIdentity';
import useIsTabletOrDesktopCheck from '@/hooks/useIsTabletOrDesktopCheck';
import useTracking from '@/hooks/useTracking';
import ContactUsFormSection from './ContactUsFormSection';
import ContactUsInfoModal from './ContactUsInfoModal';
import HelpModalContent from './HelpModalContent';
import SupportMessageModal, {useSubmitSupportCase} from './SupportMessage';

const ContactUsModal = () => {
  const {isOpen, toggleContactUsModal} = useContactUsModal();
  const {track} = useTracking();
  const {t} = useTranslation('common');
  const {data} = useIdentity();
  const submitSupportCase = useSubmitSupportCase();
  const [isSupportMessageOpen, setIsSupportMessageOpen] = useState(false);
  const isTabletOrDesktop = useIsTabletOrDesktopCheck();

  const {variation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.IntercomHelpCenter,
  );
  const showHelpCenter = variation === 'treatment';

  // eslint-disable-next-line react-hooks/purity
  const modalOpenedTimeRef = useRef<number>(Date.now());
  const hasTrackedExposureRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      modalOpenedTimeRef.current = Date.now();
      track('shown-contact-us', {
        page: window.location.pathname,
        misc_json: JSON.stringify({variant: 'modal', experiment_variation: variation}),
      });
    }
  }, [isOpen, track, variation]);

  // Exposure is the experiment's entry point, and this modal mounts on ~20 page roots: counting an
  // open rather than a mount is what keeps the analysed population to customers who saw it.
  useEffect(() => {
    if (!isOpen) {
      hasTrackedExposureRef.current = false;
      return;
    }

    // 'fallback' means the flag config never loaded, so the subject was never really assigned.
    if (hasTrackedExposureRef.current || variation === 'fallback') return;
    hasTrackedExposureRef.current = true;

    trackExperimentExposed();
  }, [isOpen, variation, trackExperimentExposed]);

  const handleModalDismiss = useCallback(() => {
    const timeSpent = Math.round((Date.now() - modalOpenedTimeRef.current) / 1000);
    track('closed-contact-us-modal', {
      page: window.location.pathname,
      misc_json: JSON.stringify({time_spent: timeSpent, experiment_variation: variation}),
    });
    toggleContactUsModal();
  }, [track, toggleContactUsModal, variation]);

  const startSupportMessage = useCallback(() => {
    setIsSupportMessageOpen(true);
    toggleContactUsModal();
  }, [toggleContactUsModal]);

  return (
    <>
      <Modal
        ariaLabel={t(
          showHelpCenter ? 'components.ContactUs.help.title' : 'components.ContactUs.info.title',
        )!}
        open={isOpen}
        onOpenChange={open => !open && handleModalDismiss()}
        size="large"
        slotProps={{
          content: {className: 'p-0'},
          ...(showHelpCenter && {dialog: {className: 'tablet:max-w-[1000px]'}}),
        }}
      >
        {showHelpCenter ? (
          <HelpModalContent
            isTabletOrDesktop={isTabletOrDesktop}
            onClose={toggleContactUsModal}
            onStartSupportMessage={startSupportMessage}
            experimentVariation={variation}
          />
        ) : (
          <div className="flex flex-col tablet:flex-row">
            <ContactUsInfoModal
              onClose={toggleContactUsModal}
              onStartSupportMessage={startSupportMessage}
              className="tablet:w-1/3"
              experimentVariation={variation}
            />
            <ContactUsFormSection
              onClose={toggleContactUsModal}
              variant="modal"
              className="tablet:w-2/3"
            />
          </div>
        )}
      </Modal>

      <SupportMessageModal
        fieldContext={{
          fullName: data?.me?.consumerAccount?.fullName ?? '',
          email: data?.me?.consumerAccount?.email ?? '',
        }}
        onClose={() => setIsSupportMessageOpen(false)}
        onSubmit={submitSupportCase}
        open={isSupportMessageOpen}
        surface="contact_us_modal"
      />
    </>
  );
};

export default memo(ContactUsModal);
